import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { createActivity, updateActivity } from "@/lib/activityApi";
import { useAuth } from "@/contexts/AuthContext";

const emptyForm = {
  account_name: "", bdr_name: "",
  pitch_created: false, pitch_created_date: "",
  pitch_submitted: false, pitch_submitted_date: "",
  meeting_scheduled: false, meeting_date: "",
  opportunity_identified: false, opportunity_description: "", estimated_deal_value: "",
  proposal_submitted: false, proposal_value: "", proposal_submission_date: "",
  deal_status: "Open", deal_closure_date: "", loss_reason: "",
  next_steps: "", notes: "",
};

const inputCls = "h-9 bg-[#0A0A0A] border-[#262626] rounded-sm text-sm font-mono-data placeholder:text-[#525252] focus-visible:ring-1 focus-visible:ring-[#0A84FF]";
const L = ({ children }) => <label className="kpi-label">{children}</label>;

export const ActivityDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      const defaults = { ...emptyForm };
      if (!initial && user?.name) {
        defaults.bdr_name = user.name; // auto-fill on create; editable
      }
      setForm(initial ? { ...emptyForm, ...initial,
        estimated_deal_value: initial.estimated_deal_value ?? "",
        proposal_value: initial.proposal_value ?? "",
      } : defaults);
    }
  }, [open, initial, user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => (v) => setForm((f) => ({ ...f, [k]: !!v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.account_name.trim() || !form.bdr_name.trim()) {
      toast.error("Account name and BDR name are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_deal_value: form.estimated_deal_value === "" ? null : Number(form.estimated_deal_value),
        proposal_value: form.proposal_value === "" ? null : Number(form.proposal_value),
      };
      // Strip empty strings so backend treats as unset
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (isEdit) {
        await updateActivity(initial.id, payload);
        toast.success("Activity updated");
      } else {
        await createActivity(payload);
        toast.success("Activity logged");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const Row = ({ flag, flagKey, dateKey, labelFlag }) => (
    <div className="grid grid-cols-12 gap-3 items-center py-2 border-b border-[#1F1F1F]">
      <div className="col-span-5 flex items-center gap-2">
        <Checkbox
          id={flagKey}
          checked={!!form[flagKey]}
          onCheckedChange={toggle(flagKey)}
          className="border-[#404040] data-[state=checked]:bg-[#0A84FF] data-[state=checked]:border-[#0A84FF]"
          data-testid={`activity-${flagKey}-checkbox`}
        />
        <label htmlFor={flagKey} className="text-sm text-white cursor-pointer select-none">{labelFlag}</label>
      </div>
      <div className="col-span-7">
        <Input
          type="date"
          value={form[dateKey] || ""}
          onChange={set(dateKey)}
          disabled={!form[flagKey]}
          className={`${inputCls} disabled:opacity-40`}
          data-testid={`activity-${dateKey}-input`}
        />
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-[#141414] border border-[#262626] rounded-sm text-white p-0 overflow-hidden shadow-[0_24px_64px_-12px_rgba(0,0,0,0.8)]"
        data-testid="activity-dialog"
      >
        <form onSubmit={onSubmit}>
          <DialogHeader className="px-6 py-4 border-b border-[#262626]">
            <DialogTitle className="font-display tracking-tight text-white text-xl">
              {isEdit ? "Update activity" : "Log BDR activity"}
            </DialogTitle>
            <div className="kpi-label">{isEdit ? "Edit fields — blanks preserve existing values." : "Capture pitch → proposal → close in one shot."}</div>
          </DialogHeader>
          <div className="px-6 py-5 max-h-[70vh] overflow-y-auto space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <L>Account Name *</L>
                <Input value={form.account_name} onChange={set("account_name")} placeholder="e.g. Siemens" className={inputCls} data-testid="activity-account-name"/>
              </div>
              <div className="flex flex-col gap-1.5">
                <L>BDR Name *</L>
                <Input value={form.bdr_name} onChange={set("bdr_name")} placeholder="e.g. Priya Sharma" className={inputCls} data-testid="activity-bdr-name"/>
              </div>
            </div>

            <div>
              <div className="kpi-label mb-2 text-[#0A84FF]">/ ACTIVITY TIMELINE</div>
              <Row flagKey="pitch_created" dateKey="pitch_created_date" labelFlag="Pitch Created"/>
              <Row flagKey="pitch_submitted" dateKey="pitch_submitted_date" labelFlag="Pitch Submitted"/>
              <Row flagKey="meeting_scheduled" dateKey="meeting_date" labelFlag="Meeting Scheduled"/>
            </div>

            <div>
              <div className="kpi-label mb-2 text-[#0A84FF]">/ OPPORTUNITY</div>
              <div className="flex items-center gap-2 mb-3">
                <Checkbox
                  id="opportunity_identified"
                  checked={!!form.opportunity_identified}
                  onCheckedChange={toggle("opportunity_identified")}
                  className="border-[#404040] data-[state=checked]:bg-[#0A84FF] data-[state=checked]:border-[#0A84FF]"
                  data-testid="activity-opportunity_identified-checkbox"
                />
                <label htmlFor="opportunity_identified" className="text-sm text-white cursor-pointer">Opportunity Identified</label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 flex flex-col gap-1.5">
                  <L>Opportunity Description</L>
                  <Textarea value={form.opportunity_description || ""} onChange={set("opportunity_description")} rows={2} placeholder="What is the opportunity?" className="bg-[#0A0A0A] border-[#262626] rounded-sm text-sm resize-none focus-visible:ring-1 focus-visible:ring-[#0A84FF]" data-testid="activity-opportunity-desc"/>
                </div>
                <div className="flex flex-col gap-1.5">
                  <L>Estimated Deal Value ($)</L>
                  <Input type="number" min="0" value={form.estimated_deal_value} onChange={set("estimated_deal_value")} placeholder="e.g. 250000" className={inputCls} data-testid="activity-estimated-deal-value"/>
                </div>
              </div>
            </div>

            <div>
              <div className="kpi-label mb-2 text-[#0A84FF]">/ PROPOSAL</div>
              <div className="flex items-center gap-2 mb-3">
                <Checkbox
                  id="proposal_submitted"
                  checked={!!form.proposal_submitted}
                  onCheckedChange={toggle("proposal_submitted")}
                  className="border-[#404040] data-[state=checked]:bg-[#0A84FF] data-[state=checked]:border-[#0A84FF]"
                  data-testid="activity-proposal_submitted-checkbox"
                />
                <label htmlFor="proposal_submitted" className="text-sm text-white cursor-pointer">Proposal Submitted</label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <L>Proposal Value ($)</L>
                  <Input type="number" min="0" value={form.proposal_value} onChange={set("proposal_value")} disabled={!form.proposal_submitted} className={`${inputCls} disabled:opacity-40`} data-testid="activity-proposal-value"/>
                </div>
                <div className="flex flex-col gap-1.5">
                  <L>Proposal Submission Date</L>
                  <Input type="date" value={form.proposal_submission_date || ""} onChange={set("proposal_submission_date")} disabled={!form.proposal_submitted} className={`${inputCls} disabled:opacity-40`} data-testid="activity-proposal-date"/>
                </div>
              </div>
            </div>

            <div>
              <div className="kpi-label mb-2 text-[#0A84FF]">/ CLOSURE</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col gap-1.5">
                  <L>Deal Status</L>
                  <Select value={form.deal_status || "Open"} onValueChange={(v) => setForm((f) => ({ ...f, deal_status: v }))}>
                    <SelectTrigger data-testid="activity-deal-status" className="h-9 bg-[#0A0A0A] border-[#262626] rounded-sm text-sm focus:ring-1 focus:ring-[#0A84FF]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626] rounded-sm">
                      {["Open", "Won", "Lost"].map((s) => (
                        <SelectItem key={s} value={s} className="text-sm font-mono-data">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <L>Closure Date</L>
                  <Input type="date" value={form.deal_closure_date || ""} onChange={set("deal_closure_date")} className={inputCls} data-testid="activity-closure-date"/>
                </div>
                <div className="flex flex-col gap-1.5">
                  <L>Loss Reason</L>
                  <Input value={form.loss_reason || ""} onChange={set("loss_reason")} placeholder="(if lost)" disabled={form.deal_status !== "Lost"} className={`${inputCls} disabled:opacity-40`} data-testid="activity-loss-reason"/>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <L>Next Steps</L>
                <Input value={form.next_steps || ""} onChange={set("next_steps")} placeholder="e.g. Follow-up call Mar 15" className={inputCls} data-testid="activity-next-steps"/>
              </div>
              <div className="flex flex-col gap-1.5">
                <L>Notes</L>
                <Input value={form.notes || ""} onChange={set("notes")} placeholder="Free-form notes" className={inputCls} data-testid="activity-notes"/>
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-[#262626] bg-[#0A0A0A]/50">
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="activity-cancel-btn"
              className="h-9 rounded-sm bg-transparent hover:bg-[#1F1F1F] text-white border border-[#262626] hover:border-[#404040]"
            >
              <span className="text-xs font-mono-data tracking-wider uppercase">Cancel</span>
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="activity-save-btn"
              className="h-9 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white"
            >
              <span className="text-xs font-mono-data tracking-wider uppercase">{saving ? "Saving…" : isEdit ? "Update" : "Log activity"}</span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityDialog;
